import { useMemo } from "react";
import useLabels from "../../hooks/useLabels";
import { useSelector } from "react-redux";
import { selectActiveWinery, selectConfigs } from "../../context/selectors";
import { addDays, format, isBefore, isEqual } from "date-fns";
import LotLabelTags from "../LotLabelTags";
import { Icon } from "../../components/Icon";
import { isExpired } from "../../services/common";
import { getCurrencySymbol, getCurrencyCode } from "../../services/currency";

// Pure: derives the per-row "sub-rows" this cell renders from the row's original
// data. No state, no side effects -- `data` is fully derived from props, so it
// must not be copied into a useEffect-driven useState (that loop is what this
// function replaces; see MultipleRowsCell below).
const buildMultipleRowsData = ({
  original,
  columnMeta,
  selector,
  currencySymbol,
  shortDateFormat,
  getLabel,
}) => {
  let tmp = [];

  if (!selector) return tmp;

  const getRightUnit = (isName = false) => {
    const unit_id = original?.unit_id || null;
    if (!original?.ingredients || !unit_id) return [];

    switch (unit_id) {
      case 1:
      case 2:
      case 4:
      case 6:
      case 7:
        // Kg
        if (isName) return "Kg";
        return 4;
      case 3:
        // L
        if (isName) return "L";
        return 3;
      case 5:
        // unit
        if (isName) return "Unit";
        return 5;
    }

    return 5;
  };

  const getRightValue = (v) => {
    switch (selector) {
      case "cost":
      case "candle_cost":
        return `${v?.cost || "—"} ${currencySymbol}`;
      case "drug_batch_number":
        return `${v?.drug_batch_number || "—"} (${v?.quantity || "—"} ${v?.warehouse_unit || ""})`;
      case "porosity":
        return `${v?.porosity || "—"} µm [${v?.porosity ? getLabel(`filterCandleType_${v?.type}`)?.toLowerCase?.() : ""}]`;
      case "candle_total":
        return `${Number(v?.total_filtered_liters) || 0}${v?.max_capacity_liters ? "/" : ""}${Number(v?.max_capacity_liters) || ""} L`;
      default:
        return v?.[selector];
    }
  };

  switch (selector) {
    case "candle_cost": {
      const candleArray = original?.[columnMeta?.array] || [];
      const totalCandleCost = candleArray?.reduce((acc, curr) => acc + (Number(curr?.cost) || 0), 0);
      tmp.push({ candle_cost: totalCandleCost > 0 ? totalCandleCost + " " + currencySymbol : "" });
      break;
    }
    case "type":
    case "supplier":
    case "batch_unitary_cost":
    case "serial_number":
    case "porosity":
    case "candle_total":
    case "batch_number":
    case "batch_quantity":
    case "expire_date":
    case "drug_batch_number": {
      const stockArray = original?.[columnMeta?.array] || [];
      if (selector === "batch_number") {
        stockArray?.forEach((s) =>
          tmp.push({ batch_number: s?.batch_number, expire_date: s?.expire_date, _isExpired: isExpired(s) })
        );
      } else if (selector === "batch_unitary_cost") {
        const perUnit = original?.unit_id ? getLabel(`drugBatchUnit_${getRightUnit()}`) : "";
        stockArray?.forEach((s) =>
          tmp.push({ batch_unitary_cost: `${s?.cost || "—"} ${currencySymbol} ${perUnit}` })
        );
      } else if (["serial_number", "porosity", "candle_cost", "candle_total", "drug_batch_number"].includes(selector)) {
        stockArray?.forEach((s) =>
          tmp.push({ [selector]: getRightValue(s), expire_date: s?.expire_date, _isExpired: isExpired(s) })
        );
      } else if (selector === "type") {
        if (stockArray?.length === 1 || (stockArray?.length === 0 && original?.type === "DETERGENT")) {
          tmp.push({ type: getLabel(`drugType_${original?.type}`) });
        } else {
          const types = new Set();
          stockArray?.reduce((_, item) => types.add(item?.type), null);
          Array.from(types)?.filter(Boolean)?.forEach((t) => tmp.push({ type: getLabel(`drugType_${t}`) }));
        }
      } else if (selector === "batch_quantity") {
        const perUnit = original?.unit_id ? getLabel(`drugBatchUnit_${getRightUnit(true)}`) : "";
        stockArray?.forEach((s) =>
          tmp.push({ batch_quantity: s?.stock ? `${s?.stock?.toFixed?.(3)} ${perUnit}` : "" })
        );
      } else if (selector === "expire_date") {
        stockArray?.forEach((s) =>
          tmp.push({ expire_date: format(new Date(s?.expire_date) || null, shortDateFormat) })
        );
      } else {
        tmp.push(...stockArray);
      }
      break;
    }
    case "ingredients": {
      const ingredientsArray = original?.[columnMeta?.array];
      if (ingredientsArray && ingredientsArray?.length > 0) {
        const sortedIngrByQuantity = [...ingredientsArray]?.sort((a, b) => b.quantity - a.quantity);
        sortedIngrByQuantity?.forEach((i) => {
          const ingredients = i?.name || "";
          const ingredientQuantity = `(${i.quantity}%)`;
          tmp.push({ ingredients: ingredients, additional: ingredientQuantity });
        });
      }
      break;
    }
    case "ranges": {
      const rangesArray = original?.[columnMeta?.array];
      if (rangesArray && rangesArray?.length > 0) {
        rangesArray?.forEach((r) => {
          // if just one color, no need to explict the corresponding ranges
          const colors = r?.colors?.length > 0 && rangesArray?.length > 1 ? r?.colors?.reduce((accumulator, current) => {
            return accumulator + (accumulator ? ", " : "") + getLabel(`lotColor_${current}`);
          }, "") : null;
          const colorsString = colors ? `[${colors}]` : "";
          tmp.push({
            ranges: `${r?.min_range ? String(r?.min_range) : "n/a"} - ${r?.max_range ? String(r?.max_range) : "n/a"} ${r?.unit || ""}`,
            additional: `${colorsString}`,
          });
        });
      }
      break;
    }
    case "states": {
      const statesArray = original?.[columnMeta?.array];
      if (statesArray && statesArray?.length > 0) {
        statesArray?.forEach((entry, index) => {
          if (index > 0) return;
          const states = entry?.states?.reduce((accumulator, current) => {
            return accumulator + (accumulator ? ", " : "") + getLabel(`lotState_${current}`);
          }, "");
          tmp.push({
            states: "",
            additional: states,
          });
        });
      }
      break;
    }
    case "client_names": {
      const clientNamesArray = original?.[columnMeta?.array];
      if (clientNamesArray) {
        tmp = clientNamesArray?.map((v) => {
          return {
            [selector]: v,
          };
        });
      }
      break;
    }
    // These selectors share one fallback chain: each tries its own array lookup
    // first, and if that's empty they all converge on the same composition-based
    // breakdown ("aggregate"'s own array is always non-empty once built, which is
    // why the original switch's fall-through into "aggregate" was effectively
    // unconditional -- reproduced explicitly here instead of relying on
    // case-fall-through, so nothing here can silently fall into "lot_label" below).
    case "unit":
    case "substance_name":
    case "quantity_unit":
    case "usernames":
    case "varieties":
    case "origins":
    case "years":
    case "aggregate": {
      let array;

      if (["unit", "substance_name", "quantity_unit"].includes(selector)) {
        array = original?.[columnMeta?.array];
        if (selector === "substance_name" && columnMeta?.array === "analysis") {
          array = array?.map((t) => {
            return { ...t, substance_name: getLabel("substance_" + t?.substance_name) };
          });
        } else if (selector === "quantity_unit") {
          array = array?.map((t) => {
            return { ...t, quantity_unit: `${t?.quantity} ${t?.unit || ""}` };
          });
        }
      } else if (selector === "usernames") {
        array = original?.[columnMeta?.array];
      } else if (["varieties", "origins", "years"].includes(selector)) {
        array = original?.[columnMeta?.array];
      }

      if (array) {
        tmp = ["unit", "substance_name", "quantity_unit", "usernames"].includes(selector)
          ? array
          : array?.map((v) => {
              return {
                [selector]: v,
              };
            });
        break;
      }

      const additional = [];
      const lot = original;
      const compositions = lot?.composition || [];
      const contributions = {};

      // Process destination's existing wine (old composition)
      compositions.forEach((comp) => {
        const { variety, year, origin, percentage } = comp;
        const key = `${variety}|${year}|${origin}`;
        const contribution = lot?.actual_quantity * (percentage / 100);

        contributions[key] = (contributions[key] || 0) + contribution;
      });

      // Convert contributions to final percentages
      let i = 0;
      let total = 0;
      for (const key in contributions) {
        const [variety, yearStr, origin] = key.split("|");
        const contribution = contributions[key];
        const percentage = (contribution / lot?.actual_quantity) * 100;
        const v = variety ? getLabel(`lotVarieties_${variety}`) : "";
        const y = yearStr ? ` [${yearStr}]` : "";
        const o = origin ? `, ${origin}` : "";
        let roundedPerc = Math.round(percentage ?? 0);

        // If it's the last item, adjust so the total is exactly 100
        if (i === Object.keys(compositions).length - 1) {
          roundedPerc = 100 - total;
        } else {
          total += roundedPerc;
        }

        const p = percentage != null ? ` (${roundedPerc}%)` : "";
        additional.push(`${v}${p}${o}${y}`);
        i += 1;
      }

      tmp = additional?.map((v) => {
        return {
          [selector]: v,
        };
      });
      break;
    }
    case "lot_label": {
      if (Array.isArray(columnMeta?.array)) {
        const labels = columnMeta?.array?.find((l) => original?.batch_id === l?.id) || null;
        if (labels && labels?.label) tmp = labels?.label || [];
      } else tmp = original?.label || [];
      break;
    }
    case "tank_name": {
      const tanks = original?.[columnMeta?.array] || [];
      tmp = tanks
        ?.map((v) => {
          return {
            [selector]: v?.[selector],
            additional: v?.additional,
          };
        })
        ?.sort((a, b) => a?.tank_name?.localeCompare(b?.tank_name, undefined, { numeric: true }));
      break;
    }
    case "tank_substate": {
      const hasMixedSubstates = original?.[columnMeta?.hasMixedSubstates];
      if (hasMixedSubstates) {
        // Mostra substates per ogni tank
        const tankSubstates = original?.[columnMeta?.array] || [];
        tmp = tankSubstates.map((ts) => ({
          [selector]: `${ts.tank_name}: ${getLabel(`lotSubstate_${ts.substate}`)}`,
        }));
      } else {
        // Mostra solo il substate del lotto
        const lotSubstate = original?.[columnMeta?.singleSubstate];
        if (lotSubstate) {
          tmp = [{
            [selector]: getLabel(`lotSubstate_${lotSubstate}`),
          }];
        }
      }
      break;
    }
    default:
      break;
  }

  return tmp;
};

// Deterministic: identity comes from whatever the row data actually offers, with
// the array index only as a last-resort tie-break. Math.random() in a key forces
// React to unmount/remount every row on every render.
const getMultipleRowKey = (item, selector, index) =>
  [
    selector,
    item?.id,
    item?.substance_id,
    item?.batch_id,
    item?.tank_id,
    item?.index,
    item?.[selector],
    index,
  ]
    .filter((value) => value !== null && value !== undefined && value !== "")
    .join("|");

const MultipleRowsCell = ({ row, column }) => {
  const columnMeta = column.columnDef.meta;
  const selector = columnMeta?.selector || "";
  const [getLabel] = useLabels();
  const configs = useSelector(selectConfigs);
  const activeWinery = useSelector(selectActiveWinery);
  const currencySymbol = getCurrencySymbol(getCurrencyCode(activeWinery));

  const data = useMemo(
    () =>
      buildMultipleRowsData({
        original: row.original,
        columnMeta,
        selector,
        currencySymbol,
        shortDateFormat: configs.shortDateFormat,
        getLabel,
      }),
    [row.original, columnMeta, selector, currencySymbol, configs.shortDateFormat, getLabel]
  );

  const parseDateDMY = (dateStr) => {
    if (!dateStr) return null;
    const [day, month, yearStr] = dateStr.split('/');

    let year = Number(yearStr);
    if (yearStr.length === 2) {
      year = year < 50 ? 2000 + year : 1900 + year;
    }

    return new Date(year, Number(month) - 1, Number(day));
  };

  const renderExpireIcons = (expireDateStr) => {
    if (!expireDateStr) return null;
    const expireDate = parseDateDMY(expireDateStr);
    if (!expireDate) return null;
    const today = new Date();
    const warningThreshold = addDays(today, 14); // oggi + 14 giorni

    if (isBefore(expireDate, today) || isEqual(expireDate, today)) {
      // Scaduto o scadenza oggi -> Unicode "!"
      return (
        <span className="additional icon" title={columnMeta?.additionalErrorMessage || 'Prodotto scaduto'}>
          &#10071;
        </span>
      );
    } else if (isBefore(expireDate, warningThreshold) || isEqual(expireDate, warningThreshold)) {
      // Scadenza entro 14 giorni -> warning FontAwesome
      return (
        <span className="additional icon" title={columnMeta?.additionalWarningMessage || 'Scadenza imminente'}>
          <Icon icon={"fa-triangle-exclamation"} color="orange" size="lg" />
        </span>
      );
    }

    return null; // nessuna icona se oltre i 14 giorni
  };


  const getValueByParam = (r) => {
    if (!r || typeof selector !== "string" || !(selector in r)) {
      return <span></span>;
    }

    const value = r[selector];
    const hasValue = value !== null && value !== undefined && value !== "";

    return (
      <span>
        {hasValue ? value : "  "}
        {r?.additional && (
          <span className="additional_span">
            {hasValue && "  "}
            {r.additional}
          </span>
        )}
        {(columnMeta?.additionalType === "expired" && selector === "expire_date") && renderExpireIcons(value)}
        {r?._isExpired && ["batch_number", "drug_batch_number"].includes(selector) && (
          <span className="error_mark" title="Batch scaduto">
            !
          </span>
        )}
      </span>
    );
  };
  if (selector === "lot_label") return <div className="lots_tags"><LotLabelTags isCell={true} tags={data} color={(data?.color || row.original?.color)?.toLowerCase?.()} /></div>

  return (
    <div>
      {data?.map?.((r, index) => {
        return (
          <div key={getMultipleRowKey(r, selector, index)}>
            {getValueByParam(r)}
          </div>
        );
      })}
    </div>
  );
};

export default MultipleRowsCell;
